import React, { useState } from 'react';
import { ShieldCheck, X } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '../../ui/card';
import { Button } from '../../ui/button';
import { SafeDatabaseUser } from '../../../types/types';
import TwoFactorSetup from './TwoFactorSetup';

interface TwoFactorPromptProps {
  user: SafeDatabaseUser;
  onContinue: () => void;
  onSetupComplete: (user: SafeDatabaseUser) => void;
}

/**
 * A modal that prompts users to set up 2FA for their account
 */
const TwoFactorPrompt: React.FC<TwoFactorPromptProps> = ({ user, onContinue, onSetupComplete }) => {
  const [showSetup, setShowSetup] = useState<boolean>(false);

  const handleSetupClick = () => {
    setShowSetup(true);
  };

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
      <Card className='w-[400px] max-w-[90vw]'>
        {showSetup ? (
          <TwoFactorSetup user={user} onComplete={onSetupComplete} onClose={onContinue} />
        ) : (
          <>
            <CardHeader className='text-center relative'>
              <div className='absolute right-2 top-2'>
                <Button variant='ghost' size='icon' onClick={onContinue} aria-label='Close'>
                  <X className='h-4 w-4' />
                </Button>
              </div>
              <div className='mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
                <ShieldCheck className='h-6 w-6 text-primary' />
              </div>
              <CardTitle>Enhance Your Account Security</CardTitle>
              <CardDescription>
                We recommend setting up two-factor authentication to protect your account from
                unauthorized access.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className='text-sm text-muted-foreground text-center'>
                Two-factor authentication adds an extra layer of security by requiring a
                verification code in addition to your password when you sign in.
              </p>
            </CardContent>
            <CardFooter className='flex justify-between gap-2'>
              <Button variant='outline' onClick={onContinue} className='flex-1'>
                Not Now
              </Button>
              <Button variant='default' onClick={handleSetupClick} className='flex-1'>
                Set Up 2FA
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
};

export default TwoFactorPrompt;
